import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { athleteAuthAPI, getErrorMessage } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CheckCircle, UserPlus } from 'lucide-react';

export default function AthleteRegisterPage() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState(null);
  const { registerAthlete, registerAthleteWithInvite } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (inviteToken) {
      validateInvite();
    }
  }, [inviteToken]);

  const validateInvite = async () => {
    try {
      const response = await athleteAuthAPI.validateInvite(inviteToken);
      setInviteData(response.data);
      setName(response.data.athlete_name);
      setEmail(response.data.athlete_email);
    } catch (error) {
      setInviteError('Invito non valido o scaduto');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (inviteToken && inviteData) {
        await registerAthleteWithInvite(name, email, password, inviteToken);
        toast.success('Registrazione completata! Benvenuto in Trainers!');
      } else {
        await registerAthlete(name, email, password);
        toast.success('Registrazione completata con successo!');
      }
      window.location.href = '/athlete/dashboard';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante la registrazione'));
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="text-[#ccff00] text-xl font-bold">Verifica invito...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#ccff00] tracking-tight mb-2">
            Trainers
          </h1>
          <p className="text-zinc-400">
            {inviteData ? 'Completa la registrazione' : 'Registrati come Atleta'}
          </p>
        </div>

        {inviteData && (
          <div className="glass-card p-4 rounded-sm border border-[#ccff00]/30 bg-[#ccff00]/5">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#ccff00]" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">Invito da {inviteData.coach_name}</p>
                <p className="text-zinc-400 text-sm">Completa la registrazione per accedere alle tue lezioni</p>
                {inviteData.athlete_code && (
                  <p className="text-zinc-500 text-xs mt-1">
                    Codice atleta: <span className="text-[#ccff00] font-mono font-bold tracking-widest">{inviteData.athlete_code}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {inviteError && (
          <div className="glass-card p-4 rounded-sm border border-red-500/30 bg-red-500/5">
            <p className="text-red-400">{inviteError}</p>
            <p className="text-zinc-400 text-sm mt-2">
              Puoi comunque registrarti normalmente o contattare il tuo allenatore per un nuovo invito.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="glass-card p-8 rounded-sm space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">Nome Completo</Label>
            <Input
              id="name"
              data-testid="athlete-register-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mario Rossi"
              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <Input
              id="email"
              data-testid="athlete-register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="il-tuo-email@esempio.it"
              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
              required
              readOnly={!!inviteData}
            />
            {inviteData && (
              <p className="text-zinc-500 text-xs">L'email è precompilata dall'invito</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <Input
              id="password"
              data-testid="athlete-register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
              required
            />
          </div>

          <Button
            data-testid="athlete-register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
          >
            {loading ? 'Registrazione...' : (inviteData ? 'Completa Registrazione' : 'Registrati')}
          </Button>

          <div className="text-center text-sm text-zinc-400">
            Hai già un account?{' '}
            <Link to="/login" className="text-[#ccff00] hover:underline font-medium">
              Accedi
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}